import { useEffect, useMemo, useState } from 'react'
import {
  Button,
  Input,
  Modal,
  RadioGroup,
  Select,
  Textarea,
  useToast,
} from '@/components/ui'
import { useAuthStore } from '@/stores/auth.ts'
import { useContractGroups, useCreateContract } from '../api/contractsApi.ts'
import type {
  ContractCategory,
  ContractDetail,
  ContractDirection,
  CreateContractInput,
} from '../types.ts'
import { CONTRACT_CATEGORY_LABELS } from '../types.ts'
import { SapPartnerPicker } from './SapPartnerPicker.tsx'

const PERSONNEL_ROLES = new Set(['admin', 'hr', 'gf'])

interface ContractFormModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated?: (contract: ContractDetail) => void
  /** Wenn gesetzt, wird die Richtung beim Oeffnen vorbelegt (Verkauf → outbound, Beschaffung → inbound). */
  defaultDirection?: ContractDirection
}

interface FormState {
  contractNumber: string
  legacyContractNumber: string
  direction: ContractDirection
  contractCategory: ContractCategory
  partySapCardCode: string
  partyName: string
  name: string
  startDate: string
  endDate: string
  signingDate: string
  billingStartDate: string
  minimumTermMonths: string
  renewalTermMonths: string
  contractGroupId: string
  billingTiming: 'advance' | 'arrears'
  notes: string
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10)
}

function makeEmpty(direction: ContractDirection = 'outbound'): FormState {
  return {
    contractNumber: '',
    legacyContractNumber: '',
    direction,
    contractCategory: 'customer',
    partySapCardCode: '',
    partyName: '',
    name: '',
    startDate: todayIso(),
    endDate: '',
    signingDate: '',
    billingStartDate: '',
    minimumTermMonths: '',
    renewalTermMonths: '',
    contractGroupId: '',
    billingTiming: 'advance',
    notes: '',
  }
}

export function ContractFormModal({ open, onOpenChange, onCreated, defaultDirection }: ContractFormModalProps) {
  const { toast } = useToast()
  const create = useCreateContract()
  const { data: contractGroups = [] } = useContractGroups()
  const [form, setForm] = useState<FormState>(() => makeEmpty(defaultDirection))
  const [error, setError] = useState<string | null>(null)
  const userRole = useAuthStore((s) => s.user?.role ?? '')
  const canUsePersonnel = PERSONNEL_ROLES.has(userRole)

  const contractGroupOptions = useMemo(
    () => [
      { value: '', label: '— Keine Gruppe —' },
      ...contractGroups.filter((g) => g.active).map((g) => ({ value: g.id, label: g.name })),
    ],
    [contractGroups],
  )

  useEffect(() => {
    if (open) {
      setForm(makeEmpty(defaultDirection))
      setError(null)
    }
  }, [open, defaultDirection])

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  function handleSave() {
    if (!form.startDate) {
      setError('Vertragsbeginn ist Pflicht.')
      return
    }
    if (!form.partySapCardCode && !form.partyName) {
      setError(
        form.direction === 'outbound'
          ? 'Kunde aus SAP wählen oder einen freien Namen eintragen.'
          : 'Lieferant aus SAP wählen oder einen freien Namen eintragen.',
      )
      return
    }

    setError(null)

    const payload: CreateContractInput = {
      contractNumber: form.contractNumber.trim() || undefined,
      legacyContractNumber: form.legacyContractNumber.trim() || null,
      direction: form.direction,
      contractCategory: form.contractCategory,
      partySapCardCode: form.partySapCardCode || null,
      partyName: form.partyName || null,
      name: form.name || null,
      startDate: form.startDate,
      endDate: form.endDate || null,
      signingDate: form.signingDate || null,
      billingStartDate: form.billingStartDate || null,
      minimumTermMonths: form.minimumTermMonths ? Number(form.minimumTermMonths) : null,
      renewalTermMonths: form.renewalTermMonths ? Number(form.renewalTermMonths) : null,
      contractGroupId: form.contractGroupId || null,
      billingTiming: form.billingTiming,
      notes: form.notes || null,
      status: 'draft',
      billingCycle: 'monthly',
    }

    create.mutate(payload, {
      onSuccess: (created) => {
        toast({ title: 'Vertrag angelegt', description: created.contractNumber, tone: 'success' })
        onCreated?.(created as unknown as ContractDetail)
      },
      onError: (err) => {
        setError(err instanceof Error ? err.message : 'Anlegen fehlgeschlagen.')
      },
    })
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      size="md"
      title="Neuer Vertrag"
      description="Stammdaten reichen für den Anfang. Positionen, Tarife und Dokumente verwaltest du anschließend in der Detailansicht."
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={() => onOpenChange(false)} disabled={create.isPending}>
            Abbrechen
          </Button>
          <Button variant="primary" size="sm" onClick={handleSave} loading={create.isPending}>
            Anlegen
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {error ? (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-small text-red-700">
            {error}
          </div>
        ) : null}

        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Vertragsnummer"
            hint="Leer = fortlaufende Nummer aus dem Counter."
            value={form.contractNumber}
            onChange={(e) => update('contractNumber', e.currentTarget.value)}
          />
          <Input
            label="Alte SAP-Vertragsnummer"
            hint="VNR aus dem alten System für die Historie."
            value={form.legacyContractNumber}
            onChange={(e) => update('legacyContractNumber', e.currentTarget.value)}
          />
        </div>

        <RadioGroup<ContractDirection>
          label="Richtung"
          value={form.direction}
          onValueChange={(v) => update('direction', v)}
          orientation="horizontal"
          options={[
            { value: 'outbound', label: 'Ausgehend (Kunde)' },
            { value: 'inbound', label: 'Eingehend (Lieferant)' },
          ]}
        />

        <Select<ContractCategory>
          label="Vertragsart"
          hint="Personalverträge sind nur für HR und GF sichtbar."
          value={form.contractCategory}
          onValueChange={(v) => update('contractCategory', v)}
          options={Object.entries(CONTRACT_CATEGORY_LABELS)
            .filter(([v]) => canUsePersonnel || v !== 'personnel')
            .map(([value, label]) => ({ value: value as ContractCategory, label }))}
        />

        <SapPartnerPicker
          label={form.direction === 'outbound' ? 'Kunde (SAP)' : 'Lieferant (SAP)'}
          hint={
            form.direction === 'outbound'
              ? 'Live-Suche über den SAP Service Layer (alle SAP-Kunden).'
              : 'Live-Suche über den SAP Service Layer (alle SAP-Lieferanten).'
          }
          value={form.partySapCardCode}
          onChange={(cardCode, name) => {
            update('partySapCardCode', cardCode)
            if (name) update('partyName', name)
          }}
          initialName={form.partyName}
        />

        <Input
          label="Name / Anzeige"
          hint="Falls SAP nicht verfügbar: freier Name. Wird in der Liste angezeigt."
          value={form.partyName}
          onChange={(e) => update('partyName', e.currentTarget.value)}
        />

        <Input
          label="Vertragsbezeichnung"
          value={form.name}
          onChange={(e) => update('name', e.currentTarget.value)}
        />

        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Abschlussdatum"
            hint="Datum der Unterzeichnung (SAP: Abschlussdatum)."
            type="date"
            value={form.signingDate}
            onChange={(e) => update('signingDate', e.currentTarget.value)}
          />
          <Input
            label="Abrechnungsbeginn"
            hint="Ab wann abgerechnet wird (SAP: Abrechnungsbeginn). Leer = wie Vertragsbeginn."
            type="date"
            value={form.billingStartDate}
            onChange={(e) => update('billingStartDate', e.currentTarget.value)}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Vertragsbeginn"
            hint="Formaler Vertragsstart — bei Bestandsverträgen das Originaldatum."
            type="date"
            value={form.startDate}
            onChange={(e) => update('startDate', e.currentTarget.value)}
            required
          />
          <Input
            label="Ende"
            hint="Leer = unbefristet"
            type="date"
            value={form.endDate}
            onChange={(e) => update('endDate', e.currentTarget.value)}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Mindestlaufzeit (Monate)"
            type="number"
            value={form.minimumTermMonths}
            onChange={(e) => update('minimumTermMonths', e.currentTarget.value)}
          />
          <Input
            label="Verlängerung (Monate)"
            type="number"
            value={form.renewalTermMonths}
            onChange={(e) => update('renewalTermMonths', e.currentTarget.value)}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Select
            label="Vertragsgruppe"
            hint="Optional — Verträge derselben Gruppe auf einer Rechnung."
            value={form.contractGroupId}
            onValueChange={(v) => update('contractGroupId', v)}
            options={contractGroupOptions}
          />
          <Select<'advance' | 'arrears'>
            label="Abrechnung im"
            value={form.billingTiming}
            onValueChange={(v) => update('billingTiming', v)}
            options={[
              { value: 'advance', label: 'Voraus' },
              { value: 'arrears', label: 'Nachgang' },
            ]}
          />
        </div>

        <Textarea
          label="Notizen"
          rows={3}
          value={form.notes}
          onChange={(e) => update('notes', e.currentTarget.value)}
        />
      </div>
    </Modal>
  )
}
